import React, { useEffect } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DeviceType, ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import {
  useWorkTagActions,
  useWorkTagState,
} from '../../shared/hooks/useApi/useWorkTag';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { WorkTagData } from '../../shared/dto/EntityRepository';
import editIcon from '../../asset/icon/edit.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import { useMemberCredit } from '../../shared/hooks/useApi/useMemberCredit';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowWorkTagList } from '../../shared/util/visibility/workTagVisibility';

const WorkTagStation: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { setStandardModal } = useModalStore();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const { deviceType } = useDeviceStore();
  const {
    tags,
    selectedTags,
    listOwnerAui,
    status,
  } = useWorkTagState();
  const {
    getWorkTagList,
    addSelectedTag,
    removeSelectedTag,
  } = useWorkTagActions();
  const {
    getMemberCreditList,
    getMemberCreditListOwner,
  } = useMemberCredit();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { requestExitEditMode } = useUnsavedChanges();
  const canShowTags = canShowWorkTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  const isMobile = deviceType === DeviceType.MOBILE;
  const isActive = isMobile && !!activeWork;

  useEffect(() => {
    if (!pageOwner?.aui) return;

    const getTagListWithApi = async () => {
      await Promise.all([
        getWorkTagList(),
        isOwner ? getMemberCreditListOwner() : getMemberCreditList(),
      ]);
    };
    getTagListWithApi();
  }, [isOwner, pageOwner?.aui]);

  const handleTags = () => {
    setStandardModal({
      modalType: ModalType.WORK_TAG_STATION,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const handleCreateTag = () => {
    setStandardModal({
      modalType: ModalType.CREATE_WORK_TAG,
      title: 'Create Tag',
      value: '',
      handleChange: () => {},
    });
  };

  const handleSelectTag = (tag: WorkTagData) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    if (selectedTags.some((t) => t.id === tag.id)) {
      removeSelectedTag(tag);
    } else {
      addSelectedTag(tag);
    }
  };

  const handleSelectTagModal = () => {
    setStandardModal({
      modalType: ModalType.SELECT_WORK_TAG_MOBILE,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const hasTags = visibleTags.length > 0;

  return (
    <WorkTagStationComp $isActive={isActive}>
      {!isEditMode && hasTags && isMobile && (
        <MobileLabelBtn onClick={handleSelectTagModal}>
          <LabelBlock>
            Label :
          </LabelBlock>
          {(isMobile && visibleSelectedTags.length > 0) ? (
            <SelectedTagCount> · {getModalMessage('TAG_SELECTED_COUNT', { count: visibleSelectedTags.length })}</SelectedTagCount>
          ) : null}
        </MobileLabelBtn>
      )}
      {isEditMode && (
        <BtnContainer>
          <HintSmallWrapper content={getHintMessage('CHANGE_TAGS')}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={editIcon}
              onClick={handleTags}
            />
          </HintSmallWrapper>
          <HintSmallWrapper content={getHintMessage('ADD_TAG')}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={handleCreateTag}
            />
          </HintSmallWrapper>
        </BtnContainer>
      )}
      {hasTags && !isMobile && (
      <WorkTagFactorContainer>
        {visibleTags.map((tag) => (
          <WorkTagFactor
          key={tag.id}
          title={tag.name}
          $isSelected={visibleSelectedTags.some((t) => t.id === tag.id)}
          onClick={() => handleSelectTag(tag)}
          >
            {tag.name}
          </WorkTagFactor>
        ))}
      </WorkTagFactorContainer>
      )}
    </WorkTagStationComp>
  );
};

const WorkTagStationComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => ($isActive ? '0%' : 'fit-content')};
  opacity: ${({ $isActive }) => ($isActive ? 0 : 1)};
  height: ${({ $isActive }) => ($isActive ? '0%' : 'calc(2vh);')};

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 12px;
  transition: all 0.7s ease-in-out;
  z-index: 3;
`;

const WorkTagFactorContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
`;

const LabelBlock = styled.div`
  width: fit-content;
  height: 12px;
  display: flex;
  align-items: center;
`;

const MobileLabelBtn = styled.div`
  display: flex;
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
`;

const SelectedTagCount = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  user-select: none;
`;

const WorkTagFactor = styled.span <{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : "transparent"};
  color: ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const BtnContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default WorkTagStation;
